import type { CrucibleOutcome, PendingCrucibleCutscenes } from '@sorrem/shared';
import type { UIRunResult } from '@sorrem-client/types';
import { useGameplay } from '@sorrem-client/ui/features/PlayPage/gameplay-context';
import type { CutsceneOutcome } from '@sorrem-client/ui/features/Cutscene';
import { useCrucibleCutscenes } from '@sorrem-client/ui/hooks';
import { useLeaveRun } from '@sorrem-client/ui/features/PlayPage/use-leave-run';
import { useCallback } from 'react';

export interface AfterRunCutsceneState {
    /** The cutscene the run ends on, once it has ended with one pending; null otherwise. */
    afterCutsceneId: string | null;
    /** Marks the cutscene seen (unless it failed to load) and goes back to Character Home. */
    finish: (outcome: CutsceneOutcome) => void;
}

/** The pending cutscene each way a run can end plays; an abandoned run plays none. */
const AFTER_RUN_CUTSCENE_SLOTS: Readonly<Record<CrucibleOutcome, keyof Pick<PendingCrucibleCutscenes, 'afterVictory' | 'afterDefeat'> | null>> = {
    victory: 'afterVictory',
    defeat: 'afterDefeat',
    abandoned: null,
};

/**
 * The crucible's after-victory or after-defeat cutscene, when one is still
 * pending for the character. The cutscene is the run's ending: it goes
 * straight back to Character Home, with no results screen after it.
 */
export function useAfterRunCutscene(runResult: UIRunResult | null): AfterRunCutsceneState {
    const leaveRun = useLeaveRun();
    // A debug preview has no crucible, so no cutscenes.
    const { crucibleId } = useGameplay();
    const { pending, markSeen } = useCrucibleCutscenes(crucibleId);
    const slot = runResult === null ? null : AFTER_RUN_CUTSCENE_SLOTS[runResult.outcome];
    const afterCutsceneId = slot === null ? null : pending?.[slot] ?? null;

    const finish = useCallback((outcome: CutsceneOutcome): void => {
        // One that failed to load stays unseen, so it plays the next time.
        if (afterCutsceneId !== null && outcome !== 'failed') {
            void markSeen(afterCutsceneId);
        }
        void leaveRun('home');
    }, [afterCutsceneId, leaveRun, markSeen]);

    return { afterCutsceneId, finish };
}
